import { defineStore } from 'pinia'
import { ref } from 'vue'

import { usePopupAnalytics } from '@/composables/use-popup-analytics'
import { useAppStore } from '@/store/app-store'
import { useLoggerStore } from '@/store/logger'
import { ONE_TAP_WEIGHT, useNotificationsQueue } from '@/store/notification-que/notifications-queue'
import { useUserStore } from '@/store/user-store/user-store'

const NOTIFICATION = 'auth-notification'
const POPUP_ID = 'native-ios-google-sign-in-modal'
const MIN_IOS_VERSION = '1.0.6'
const AUTO_SHOW_DELAY_MS = 1000

export const NATIVE_IOS_GOOGLE_SIGN_IN_LOGIN_SOURCE = 'native-ios-google-sign-in-popup'

export const useModalNativeIosGoogleSignInStore = defineStore(
    'modal_native_ios_google_sign_in',
    () => {
        const loggerStore = useLoggerStore()
        const popupAnalytics = usePopupAnalytics(POPUP_ID)

        const isOpened = ref(false)
        const shownThisSession = ref(false)

        let onDismiss: (() => void) | undefined
        let exit: { reason: string; dismissed: boolean } | null = null
        let autoShowTimeoutId: ReturnType<typeof setTimeout> | null = null

        function promote(onDismissed?: () => void) {
            onDismiss = onDismissed

            const appStore = useAppStore()
            const userStore = useUserStore()
            const queue = useNotificationsQueue()

            if (shownThisSession.value) {
                return
            }

            if (!appStore.isDebugMode && !appStore.isVersionAtLeast({ ios: MIN_IOS_VERSION })) {
                return
            }

            clearAutoShow()
            autoShowTimeoutId = setTimeout(() => {
                autoShowTimeoutId = null
                requestSlot(queue, userStore)
            }, AUTO_SHOW_DELAY_MS)
        }

        function clearAutoShow() {
            if (autoShowTimeoutId) {
                clearTimeout(autoShowTimeoutId)
                autoShowTimeoutId = null
            }
        }

        function requestSlot(
            queue: ReturnType<typeof useNotificationsQueue>,
            userStore: ReturnType<typeof useUserStore>,
        ) {
            queue.request({
                name: NOTIFICATION,
                weight: ONE_TAP_WEIGHT,
                onOpen: () => {
                    if (userStore.isAuthorized) {
                        queue.remove(NOTIFICATION)
                        return
                    }

                    isOpened.value = true
                    shownThisSession.value = true
                    popupAnalytics.shown({ trigger: 'page-load' })
                    loggerStore.logInterfaceEvents({
                        eventName: 'interface_show',
                        interface_target: 'modal-native-ios-google-sign-in',
                    })
                },
                onClose: (reason) => {
                    const pending = reason === 'removed' ? exit : null
                    exit = null

                    if (!isOpened.value) {
                        return
                    }

                    isOpened.value = false

                    if (userStore.isAuthorized) {
                        popupAnalytics.closed('auth')
                        return
                    }

                    popupAnalytics.closed(pending?.reason ?? reason)

                    if (pending?.dismissed) {
                        onDismiss?.()
                    }
                },
            })
        }

        function leave(reason: string, dismissed: boolean) {
            exit = { reason, dismissed }
            useNotificationsQueue().remove(NOTIFICATION)
            exit = null
        }

        function close(reason: string) {
            leave(reason, true)
        }

        function startAuth() {
            popupAnalytics.clicked('google')
            exit = { reason: 'native-cancelled', dismissed: true }
        }

        function finishAuth(isSuccess: boolean) {
            if (isSuccess) {
                shownThisSession.value = false
            }

            leave(isSuccess ? 'auth' : 'auth-failed', !isSuccess)
        }

        function dispose() {
            clearAutoShow()
            leave('disposed', false)
        }

        return {
            isOpened,
            shownThisSession,
            promote,
            dispose,
            close,
            startAuth,
            finishAuth,
        }
    },
    {
        persist: {
            key: 'modal_native_ios_google_sign_in',
            storage: import.meta.env.SSR ? undefined : sessionStorage,
            pick: ['shownThisSession'],
        },
    },
)
